/**
 * @file NotificationBell.jsx
 * @location src/components/layout/NotificationBell.jsx
 * @role Topbar 종 아이콘 — 본인이 응답해야 할 항목 inbox orchestrator
 *
 * 인박스 데이터는 useNotificationInboxes 훅에서, 섹션 렌더링은 자식
 * 컴포넌트(TaskInboxes) 에서 처리.
 * (검수대기·@멘션·진척정체 razor ㉓ 폐기 — MiscInboxes 제거)
 */
import { useState, useEffect, useRef } from 'react';
import { useApp } from '../../store/AppContext';
import { Icon } from '../ui/Icon';
import { EmptyState } from '../ui/sharedui/feedback';
import { useNotificationInboxes } from './notificationbell/useNotificationInboxes';
import { TopStallSection } from './notificationbell/TopStallSection';
import { TaskInboxes }   from './notificationbell/TaskInboxes';

export function NotificationBell({ onToast }) {
  const { act } = useApp();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const inboxes = useNotificationInboxes();
  const { total } = inboxes;

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const close = () => setOpen(false);
  const goToTasks = () => {
    act('SET_NAV', { nav: 'tasks' });
    close();
  };
  const openReview = (id) => {
    act('SET_UI', { patch: { modalProg: true, progTargetId: id } });
    close();
  };

  return (
    <div className="nbell-wrap" ref={ref}>
      <button
        className={`nbell${total > 0 ? ' has-items' : ''}`}
        title={total > 0 ? `대응 항목 ${total}건` : '대응 항목 없음'}
        onClick={() => setOpen(v => !v)}
        aria-label={`알림 ${total}건`}
      >
        <Icon name="bell" size={16} />
        {total > 0 && <span className="nbell-badge">{total}</span>}
      </button>

      {open && (
        <div className="nbell-panel">
          {total === 0 && (
            <EmptyState icon="check" tone="positive" title="대응 항목이 없습니다" />
          )}

          <TopStallSection
            topStall={inboxes.topStall}
            onGoToTasks={goToTasks}
          />

          {/* 최우선 정체 섹션과 중복 노출 방지 — topStallKeys 매칭(src+stage+id) 항목만 하위에서 제외 */}
          {(() => {
            const tk = inboxes.topStallKeys;
            const skip = (src, stage) => (t) => !tk.has(`${src}-${stage}-${t.id}`);
            return (
              <TaskInboxes
                unack={inboxes.unack.filter(skip('self', 'unack'))}
                subordinateStalls={inboxes.subordinateStalls.filter(t => !tk.has(`sub-${t._stage}-${t.id}`))}
                cancelledToMe={inboxes.cancelledToMe}
                upcomingDue={inboxes.upcomingDue}
                onGoToTasks={goToTasks}
                onOpenReview={openReview}
                onCloseBell={close}
                onToast={onToast}
              />
            );
          })()}
        </div>
      )}
    </div>
  );
}
